import { BranchTiny } from './branch';
import { LocationCamera } from './camera';
import { LocationTiny } from './location';
import { ValuesOf } from './utils';

export const DEVICE_MODE = {
  SHOPPER_SENSE: 'shopper_sense',
  PANO_BRIDGE: 'pano_bridge',
  SHOPPER_SENSE_REMOTE_ENV: 'shopper_sense_remote_env',
  SHOPPER_SENSE_AND_PANO_BRIDGE: 'shopper_sense_and_pano_bridge',
} as const;

export const PANO_BRIDGE_PROTOCOL = {
  UDP: 'udp',
  TCP: 'tcp',
} as const;

export const EVENT_FORMAT = {
  RORC_CSV: 'RORC_CSV',
  ISS45: 'ISS45',
  LOC_OPENEYE: 'LOC_OPENEYE',
  INTERSTATE4690: 'INTERSTATE_4690',
  NCR_RPOS: 'NCR_RPOS',
} as const;

export type EventFormat = ValuesOf<typeof EVENT_FORMAT>;
export type DeviceMode = ValuesOf<typeof DEVICE_MODE>;
export type PanoBridgeProtocol = ValuesOf<typeof PANO_BRIDGE_PROTOCOL>;
// I'm not even sure half the configs here should be displayed
export type SipConfig = {
  dial_out: string;
  dial_out_mode: string;
  call_volume: number;
};

export type PanoBridgeConfig = {
  protocol: PanoBridgeProtocol;
  port: number;
  format: EventFormat;
};

export type ShopperSenseConfig = {
  playlist_label: string;
  primary_stream_lan_url: string;
  sip_config: SipConfig;
};

export type GenericNetworkConfig = {
  mode?: 'dhcp' | 'static';
  address?: string;
  gateway?: string;
  dns?: string; // in our case DNS is just 1 IP, not a comma separated list or array
};

export type Eth0NetworkConfig = GenericNetworkConfig;
export type UsbEthConfig = GenericNetworkConfig & {
  // IDK if this is set in the UI or hardcoded in the backend so need to check
  match_property?: string;
};
export type EdgeDeviceNetworkConfig = {
  eth0?: Eth0NetworkConfig;
  usb_eth?: UsbEthConfig;
};

// values can be null for configs when a device is still not configured
export type EdgeDeviceConfig = {
  device_mode: DeviceMode;
  pano_bridge?: PanoBridgeConfig;
  // currently shopper_sense config will always be null because we are never setting it
  shopper_sense?: ShopperSenseConfig;
  network?: EdgeDeviceNetworkConfig;
};

// An edge device may not be connected to a camera, hence why camera related information is optional
export type EdgeDevice = {
  id: string;
  mac_address?: string;
  serial_number?: string;
  draw_boxes: boolean;
  created_at: Date;
  location?: LocationTiny;
  branch?: BranchTiny; // currently we assume the branch will be the same for any mode
  camera?: EdgeDeviceCamera;
  // If the device has a camera, it should be assumed shopper_sense mode or shopper_sense_and_pano_bridge
  // Config can be null because this value is new and older devices may not be backfilled
  config?: EdgeDeviceConfig;
};

export type EdgeDeviceActionResponse = {
  job_id?: string;
  job_arn?: string;
  status: 'created' | 'skipped';
  message?: string;
  current_version?: string;
  target_version?: string;
  machine_type?: string;
};

export type EdgeDeviceCamera = Required<
  Pick<LocationCamera, 'id' | 'provider_branch_id' | 'provider_camera_id' | 'camera_name' | 'branch_camera_id'>
> & { location: LocationTiny }; // the location for the camera can differ from the top-level location (used by pano_bridge)

export type ConnectEdgeDeviceToCameraRequest = {
  branch_id: number;
  location_id: number;
  provider_camera_id?: string;
  location_camera_id?: string;
};

export type ConfigurePanoBridgeRequest = PanoBridgeConfig & {
  location_id: number;
};
